// ts-rest contract for the Ops Reporting report catalogue (issue #289).
//
// The catalogue is a STATIC, code-defined firm-config catalogue of report
// DEFINITIONS — analogous to the react-pdf template registry — NOT ingested
// domain data and NOT an ontology Object Type (ADR-0009, ADR-0002). It is the
// fixed list of reports the firm produces (regulatory returns, client
// statements, operational summaries, management MI); the rows describe report
// types, not generated artefacts. The controller therefore returns a code
// constant rather than reading through ctx.read.
//
// This contract is a presentation projection of that catalogue: the exact
// shape the Ops -> Reporting -> Report Library tab renders.
//
// Endpoints under /api/ops/reporting:
//   GET /api/ops/reporting/catalogue — read the static report catalogue

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Data shapes
// ---------------------------------------------------------------------------

export const reportCategorySchema = z.enum(['Regulatory', 'Client', 'Operational', 'Management']);
export type ReportCategory = z.infer<typeof reportCategorySchema>;

export const reportFrequencySchema = z.enum([
    'Daily',
    'Weekly',
    'Monthly',
    'Quarterly',
    'Annual',
    'Ad hoc',
]);
export type ReportFrequency = z.infer<typeof reportFrequencySchema>;

export const reportFormatSchema = z.enum(['PDF', 'CSV', 'XLSX']);
export type ReportFormat = z.infer<typeof reportFormatSchema>;

/**
 * A single report definition in the firm's catalogue. A presentation
 * projection — `id` is a stable slug the UI keys on; `regulator` is carried
 * only for Regulatory reports (null otherwise).
 */
export const reportDefinitionSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: reportCategorySchema,
    description: z.string(),
    frequency: reportFrequencySchema,
    format: reportFormatSchema,
    /** The responsible firm function (e.g. 'Compliance', 'Operations'). */
    owner: z.string(),
    /** The regulator a Regulatory report is filed with; null for non-regulatory reports. */
    regulator: z.string().nullable(),
});
export type ReportDefinitionDto = z.infer<typeof reportDefinitionSchema>;

export const reportCatalogueResponseSchema = z.object({
    reports: z.array(reportDefinitionSchema),
});
export type ReportCatalogueResponse = z.infer<typeof reportCatalogueResponseSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const reportingContract = c.router(
    {
        getCatalogue: {
            method: 'GET',
            path: '/ops/reporting/catalogue',
            responses: {
                200: reportCatalogueResponseSchema,
            },
            summary: 'Read the static firm report catalogue (report definitions, not artefacts)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
